import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ScheduleBookingForm from "../components/landing/ScheduleBookingForm";
import Footer from "../components/landing/Footer";
import LandingNavbar from "../components/landing/LandingNavbar";
import usePageSeo from "../hooks/usePageSeo";
import { comMarketing, KW3_SHOPIFY } from "../data/seo/kw3OnPage";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import { SHOPIFY_SUPPORT_FAQS } from "../data/seo/faqs";
import { buildServiceSchema } from "../data/seo/structuredData";

type Channel = "voice" | "chat";

const keyFeatures = [
    {
        title: "Real-Time Shopify Integration",
        description: "Securely connects to your store to look up products, orders and customers instantly — same data, on the call or in chat.",
    },
    {
        title: "Voice + Chat in One Agent",
        description: "Embed live chat on your storefront and answer the phone with the same AI agent — one knowledge base, one set of policies.",
    },
    {
        title: "In-Call Order Lookup",
        description: "Callers can ask 'Where is order #1042?' and the agent fetches the live status from Shopify before finishing the sentence.",
    },
    {
        title: "Outbound Recovery Calls",
        description: "Auto-call Shopify customers when carts are abandoned, high-value orders land, or shipments are out for delivery.",
    },
    {
        title: "Smart Refund & Return Handling",
        description: "Applies your store policies before approving requests — and never promises a refund on the call without a human ticket.",
    },
    {
        title: "Automated Order Tracking",
        description: "Handles 'where is my order' queries 24/7 across phone, chat and email — no human needed for repetitive lookups.",
    },
    {
        title: "Warm Human Transfer",
        description: "Hands sensitive or complex calls to your support team mid-conversation, with the full transcript and order context attached.",
    },
    {
        title: "24/7 Multilingual Coverage",
        description: "Answers calls and chats around the clock in 16+ languages — never miss a customer, even outside business hours.",
    },
];

const primaryKeywords = [
    "Shopify AI customer support",
    "Shopify AI voice agent",
    "AI phone support for Shopify",
    "AI chatbot for Shopify",
    "Shopify abandoned cart recovery calls",
    "Automated Shopify support",
    "Shopify support automation",
    "Ecommerce AI support",
    "Shopify helpdesk automation",
    "AI agent for Shopify",
];

interface ScriptTurn {
    speaker: "AI" | "Customer";
    text: string;
}

const voiceScript: ScriptTurn[] = [
    { speaker: "AI", text: "Hi, you've reached Acme Apparel — this is Maya. How can I help?" },
    { speaker: "Customer", text: "Hey, where's my order #1042?" },
    { speaker: "AI", text: "One sec — pulling that up from Shopify…" },
    { speaker: "AI", text: "Shipped Tuesday via FedEx, out for delivery today by 6 PM. Want a tracking SMS?" },
    { speaker: "Customer", text: "Yes please. Also — can I cancel #1051?" },
    { speaker: "AI", text: "It hasn't shipped yet, so I can cancel it now and refund $89.50 to your original card." },
];

const chatScript: ScriptTurn[] = [
    { speaker: "Customer", text: "Hi, do you have the linen shirt in size M?" },
    { speaker: "AI", text: "Yes — Sand and Olive are in stock in M. Want me to share the link?" },
    { speaker: "Customer", text: "Sand please. And what's your return policy?" },
    { speaker: "AI", text: "Free returns within 30 days. Here's the Sand linen shirt — added a 10% first-order code." },
    { speaker: "Customer", text: "Perfect, thanks!" },
    { speaker: "AI", text: "Anytime. I'll text you when it ships." },
];

const outboundTriggers = [
    {
        title: "Cart Abandoned",
        topic: "carts/update",
        description: "Send a recovery SMS with the cart link, then call when the cart value crosses your minimum.",
        accent: "from-orange-500 to-pink-500",
    },
    {
        title: "Checkout Abandoned",
        topic: "checkouts/create",
        description: "Call when a checkout starts but never converts — recover the sale while it's still warm.",
        accent: "from-pink-500 to-rose-500",
    },
    {
        title: "High-Value Order Placed",
        topic: "orders/create",
        description: "Confirmation call right after a high-AOV order lands — verify, upsell, and reduce chargebacks.",
        accent: "from-amber-500 to-orange-500",
    },
    {
        title: "Shipment Created",
        topic: "fulfillments/create",
        description: "Proactive 'your order is on the way' call with live tracking — cuts WISMO tickets dramatically.",
        accent: "from-blue-500 to-cyan-500",
    },
    {
        title: "Refund Initiated",
        topic: "refunds/create",
        description: "Confirmation call when a refund is created — clear expectations, fewer 'where's my money' tickets.",
        accent: "from-emerald-500 to-teal-500",
    },
    {
        title: "Post-Delivery CSAT",
        topic: "time-relative",
        description: "Automated CSAT call N days after a Shopify order is fulfilled — capture feedback and review intent.",
        accent: "from-indigo-500 to-purple-500",
    },
];

const shopifyUseCases = [
    {
        title: "Recover abandoned carts & revenue",
        description:
            "When a cart or checkout is abandoned, FloGPT texts the cart link and calls the shopper to win the sale back — automatically, the moment it happens.",
        href: "#outbound-triggers",
        cta: "See cart-recovery triggers",
        accent: "from-orange-500 to-pink-500",
    },
    {
        title: "Automate order tracking",
        description:
            "\u201CWhere is my order?\u201D calls and chats resolve themselves 24/7 — the agent reads live Shopify fulfillment status and texts the tracking link without a human.",
        href: "#voice-and-chat",
        cta: "Watch it in action",
        accent: "from-blue-500 to-cyan-500",
    },
    {
        title: "Improve product discovery",
        description:
            "Shoppers ask for sizes, colors, or \u201Csomething like this\u201D and the agent searches your live catalog, recommends in-stock products, and shares the link.",
        href: "#voice-and-chat",
        cta: "See a chat example",
        accent: "from-emerald-500 to-teal-500",
    },
    {
        title: "Sell on the call",
        description:
            "On confirmation and high-value calls the agent verifies details, answers objections, and upsells — turning routine support contacts into revenue.",
        href: "#outbound-triggers",
        cta: "See outbound calls",
        accent: "from-amber-500 to-orange-500",
    },
];

export default function ShopifySupportLandingPage() {
    const [channel, setChannel] = useState<Channel>("voice");
    const [visibleTurns, setVisibleTurns] = useState(0);

    const activeScript = channel === "voice" ? voiceScript : chatScript;

    const kw3 = comMarketing();

    usePageSeo({
        title: kw3 ? KW3_SHOPIFY.title : "Shopify AI Voice & Chat Support | Automate Calls + Tickets 24/7 | FloGPT",
        description:
            "AI voice and chat support for Shopify stores. Answers calls, resolves order tracking, refund and return tickets 24/7, and runs outbound recovery calls on cart-abandon, fulfillment and refund events.",
        keywords: primaryKeywords.join(", "),
        canonical: "/shopify-ai-customer-support",
    });

    const serviceSchema = useMemo(
        () =>
            buildServiceSchema({
                name: "FloGPT for Shopify — AI Voice & Chat Customer Support",
                description:
                    "AI agents for Shopify that answer voice and chat customer support 24/7 — order lookup, tracking, refunds, returns, and product recommendations grounded in your live catalog. Outbound calls on cart-abandon and fulfillment events.",
                path: "/shopify-ai-customer-support",
                serviceType: "Shopify Customer Support Automation",
                features: [
                    "Live order, tracking, and shipment lookup",
                    "Automated refunds and returns within your store policy",
                    "Live product catalog search and recommendations",
                    "Cart-abandon outbound recovery calls",
                    "Fulfillment and refund event triggers",
                    "Multilingual chat (50+ languages) and voice (16 languages)",
                    "Works with Shopify and Shopify Plus",
                    "Headless storefront support (Liquid, Hydrogen, Next.js)",
                ],
            }),
        [],
    );

    useEffect(() => {
        setVisibleTurns(0);
    }, [channel]);

    useEffect(() => {
        if (visibleTurns >= activeScript.length) {
            const reset = window.setTimeout(() => setVisibleTurns(0), 3500);
            return () => window.clearTimeout(reset);
        }
        const timer = window.setTimeout(() => {
            setVisibleTurns((n) => n + 1);
        }, visibleTurns === 0 ? 600 : 1500);
        return () => window.clearTimeout(timer);
    }, [visibleTurns, activeScript.length]);

    return (
        <div className="bg-slate-950 text-slate-100 min-h-screen overflow-x-hidden">
            <LandingNavbar />

            <main>
                <section className="relative overflow-hidden border-b border-slate-900 pt-24 sm:pt-28" id="features">
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-orange-500/20 blur-3xl" />
                        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-pink-500/10 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-5xl px-6 lg:px-8 py-16 lg:py-24">
                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center"
                        >
                            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                Voice Calls · Live Chat · Outbound Triggers
                            </div>
                            <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-[3.35rem] lg:leading-[1.05]">
                                AI Voice & Chat Support for{" "}
                                <motion.span
                                    className="inline-block"
                                    animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                    style={{
                                        background: "linear-gradient(90deg, #f97316, #ec4899, #3b82f6, #f97316)",
                                        backgroundSize: "200% 100%",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        backgroundClip: "text",
                                    }}
                                >
                                    Shopify
                                </motion.span>{" "}
                                Stores That Resolves Tickets Automatically
                            </h1>
                            {kw3 ? (
                                <p className="mx-auto mt-6 max-w-3xl text-lg text-slate-300 leading-relaxed">
                                    {KW3_SHOPIFY.intro}{" "}
                                    <Link to="/ai-chat-widget" className="font-medium text-orange-300 hover:text-orange-200">
                                        AI chat widget
                                    </Link>
                                    .
                                </p>
                            ) : null}
                            <p className={`mx-auto max-w-3xl text-lg text-slate-300 leading-relaxed ${kw3 ? "mt-4" : "mt-6"}`}>
                                One autonomous AI agent for your{" "}
                                <span className="relative inline-block pb-1 font-semibold text-orange-100">
                                    Shopify
                                    <motion.span
                                        className="absolute left-0 right-0 -bottom-0.5 h-[3px] rounded-full"
                                        initial={{ scaleX: 0, opacity: 0.5 }}
                                        whileInView={{ scaleX: 1, opacity: 1 }}
                                        transition={{ duration: 0.7, ease: "easeOut" }}
                                        viewport={{ once: true, amount: 0.6 }}
                                        style={{
                                            transformOrigin: "left",
                                            background: "linear-gradient(90deg, #f97316, #ec4899, #3b82f6, #f97316)",
                                            backgroundSize: "200% 100%",
                                        }}
                                        animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                                    />
                                </span>{" "}
                                store — answers calls, replies in chat, and dials customers on cart-abandon and fulfillment events. Order tracking, refunds, returns and product questions, handled 24/7 without human intervention.
                            </p>

                            <div className="mt-8 relative">
                                <div className="absolute -inset-2 rounded-3xl bg-gradient-to-r from-orange-500/25 to-pink-500/25 blur-xl" />
                                <div className="relative overflow-hidden rounded-3xl border border-slate-700/80 bg-slate-900/80 shadow-2xl">
                                    <video
                                        className="h-full w-full object-cover"
                                        src="/videos/shopify-hero-demo.mp4"
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        controls
                                        preload="metadata"
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                </div>
                            </div>

                            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                                <a
                                    href="#book-free-demo"
                                    className="inline-flex rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-0.5"
                                >
                                    Book a Free Demo
                                </a>
                                <a
                                    href="#voice-and-chat"
                                    className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                >
                                    See voice + chat in action
                                </a>
                            </div>
                            <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-slate-400">
                                <span>· Inbound + outbound calls</span>
                                <span>· Storefront chat widget</span>
                                <span>· Shopify webhooks &amp; outbound triggers</span>
                            </div>
                        </motion.div>
                    </div>
                </section>

                <section id="cost-reduction" className="mx-auto max-w-4xl px-6 lg:px-8 py-14 lg:py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="space-y-6"
                    >
                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                    Reduce Support Costs by Up to 60%
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                If your Shopify store receives 1,000 tickets and inbound calls per month and 60% are repetitive:
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">
                                Our AI agent automatically resolves hundreds of tickets and answers every call — saving thousands in support and BPO costs while increasing response speed across phone, chat and email.
                            </p>
                        </article>

                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                    Why This Is Different From a Shopify Chatbot or IVR
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Most Shopify chatbots use predefined scripts. Most phone systems make callers press 1.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">Our Agentic AI Support System:</p>
                            <ul className="mt-4 space-y-3 text-slate-200">
                                <li className="flex items-start gap-3">
                                    <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] bg-gradient-to-br from-orange-400 via-pink-400 to-blue-400 shadow-[0_0_0_3px_rgba(251,146,60,0.15)]" />
                                    <span>Understands free-form speech and chat in 16+ languages</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] bg-gradient-to-br from-orange-400 via-pink-400 to-blue-400 shadow-[0_0_0_3px_rgba(251,146,60,0.15)]" />
                                    <span>Looks up Shopify orders, products and customers live, mid-call</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] bg-gradient-to-br from-orange-400 via-pink-400 to-blue-400 shadow-[0_0_0_3px_rgba(251,146,60,0.15)]" />
                                    <span>Executes actions — cancel, track, refund-ticket — across phone and chat</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] bg-gradient-to-br from-orange-400 via-pink-400 to-blue-400 shadow-[0_0_0_3px_rgba(251,146,60,0.15)]" />
                                    <span>Warm-transfers to a human supervisor the second it's needed</span>
                                </li>
                            </ul>
                            <p className="mt-4 text-slate-100 font-medium">Works like a trained support agent — on every channel.</p>
                        </article>
                    </motion.div>
                </section>

                {/* WHAT SHOPIFY STORES USE FLOGPT FOR — outcome-oriented use cases */}
                <section id="use-cases" className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-20">
                    <div className="text-center max-w-3xl mx-auto">
                        <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                            Shopify use cases
                        </div>
                        <h2 className="mt-5 text-3xl sm:text-4xl font-bold text-white">
                            What Shopify stores use{" "}
                            <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                FloGPT
                            </span>{" "}
                            for
                        </h2>
                        <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                            Beyond answering tickets, the same Shopify-aware agent recovers abandoned carts, automates order tracking, helps shoppers find products, and closes sales — across phone and chat.
                        </p>
                    </div>

                    <div className="mt-10 grid gap-6 md:grid-cols-2">
                        {shopifyUseCases.map((useCase, index) => (
                            <motion.article
                                key={useCase.title}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                viewport={{ once: true, amount: 0.2 }}
                                className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-1 hover:border-orange-400/40"
                            >
                                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${useCase.accent}`} />
                                <h3 className="text-xl font-semibold text-white">{useCase.title}</h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">{useCase.description}</p>
                                <a
                                    href={useCase.href}
                                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-300 transition hover:text-orange-200"
                                >
                                    {useCase.cta}
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                                    </svg>
                                </a>
                            </motion.article>
                        ))}
                    </div>
                </section>

                {/* VOICE + CHAT IN ACTION — animated demo with channel toggle */}
                <section id="voice-and-chat" className="relative overflow-hidden border-y border-slate-900 bg-slate-950/60">
                    <div className="absolute inset-0 -z-10 opacity-40">
                        <div className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-orange-500/15 blur-3xl" />
                        <div className="pointer-events-none absolute -right-32 bottom-10 h-72 w-72 rounded-full bg-blue-500/15 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-20">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true, amount: 0.3 }}
                            >
                                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                    Same agent. Phone + Chat.
                                </div>
                                <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                    One Shopify-trained AI on your{" "}
                                    <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                        phone line and storefront
                                    </span>
                                </h2>
                                <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                    Customers don't care which channel they reach you on — they care about the answer. FloGPT runs the same Shopify-aware agent on your inbound phone number and your storefront chat widget, so a refund question gets the same answer at 2 AM whether it comes by call or chat.
                                </p>
                                <ul className="mt-6 space-y-3 text-slate-200">
                                    <li className="flex items-start gap-3">
                                        <svg className="mt-1 h-5 w-5 shrink-0 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span><span className="font-semibold text-white">In-call Shopify lookups</span> — products, orders, fulfillments, customers, all from a real voice conversation.</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <svg className="mt-1 h-5 w-5 shrink-0 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span><span className="font-semibold text-white">Cancel &amp; track on the call</span> — the agent can cancel an unfulfilled order or read the live tracking status before the caller finishes the question.</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <svg className="mt-1 h-5 w-5 shrink-0 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span><span className="font-semibold text-white">SMS follow-up built in</span> — tracking links, recovery codes and confirmations get texted while the call is still live.</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <svg className="mt-1 h-5 w-5 shrink-0 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span><span className="font-semibold text-white">Refund-safe by default</span> — never promises money on the call; always opens a high-priority human ticket for refund requests.</span>
                                    </li>
                                </ul>
                                <div className="mt-8 flex flex-wrap gap-3">
                                    <Link
                                        to="/ai-voice-agents"
                                        className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Explore voice agents
                                    </Link>
                                </div>
                            </motion.div>

                            {/* Animated demo with channel toggle */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.97 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                viewport={{ once: true, amount: 0.3 }}
                                className="relative"
                            >
                                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-orange-500/20 to-blue-500/20 blur-2xl" />
                                <div className="relative overflow-hidden rounded-3xl border border-slate-700/80 bg-slate-900/80 shadow-2xl">
                                    {/* Channel toggle */}
                                    <div className="flex items-center justify-between gap-4 border-b border-slate-800 p-4 sm:p-5">
                                        <div className="flex items-center gap-3">
                                            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-semibold text-sm">
                                                M
                                            </div>
                                            <div>
                                                <div className="text-white font-semibold text-sm">Maya · Acme Apparel</div>
                                                <div className="text-[11px] text-slate-400">
                                                    {channel === "voice" ? "Live on +1 (555) 010-2025" : "Live on storefront chat"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="inline-flex rounded-xl border border-slate-800 bg-slate-950/60 p-1 text-xs">
                                            <button
                                                type="button"
                                                onClick={() => setChannel("voice")}
                                                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-semibold transition ${
                                                    channel === "voice"
                                                        ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow"
                                                        : "text-slate-400 hover:text-slate-200"
                                                }`}
                                            >
                                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h2.28a2 2 0 011.94 1.515l.62 2.48a2 2 0 01-.45 1.95L8.09 10.91a11 11 0 005 5l1.97-1.3a2 2 0 011.95-.45l2.48.62A2 2 0 0121 16.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                                Voice call
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setChannel("chat")}
                                                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-semibold transition ${
                                                    channel === "chat"
                                                        ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow"
                                                        : "text-slate-400 hover:text-slate-200"
                                                }`}
                                            >
                                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-3.5-7.1L21 3v6h-6" />
                                                </svg>
                                                Live chat
                                            </button>
                                        </div>
                                    </div>

                                    {/* Status row */}
                                    <div className="flex items-center justify-between border-b border-slate-800 px-4 sm:px-5 py-2.5 text-[11px] text-slate-400">
                                        <div className="flex items-center gap-2">
                                            <motion.span
                                                className={`h-2 w-2 rounded-full ${channel === "voice" ? "bg-emerald-400" : "bg-sky-400"}`}
                                                animate={{ opacity: [0.4, 1, 0.4] }}
                                                transition={{ duration: 1.4, repeat: Infinity }}
                                            />
                                            {channel === "voice" ? "Live call · 00:38" : "Active chat · response 0.6s"}
                                        </div>
                                        <span>Pipeline: Shopify Support v4</span>
                                    </div>

                                    {/* Script body */}
                                    <div className="space-y-3 p-4 sm:p-5 min-h-[320px]">
                                        <AnimatePresence mode="popLayout">
                                            {activeScript.slice(0, visibleTurns).map((turn, i) => (
                                                <motion.div
                                                    key={`${channel}-${i}`}
                                                    initial={{ opacity: 0, y: 8 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                    className={`w-fit max-w-[85%] rounded-2xl px-4 py-2 text-sm shadow ${
                                                        turn.speaker === "AI"
                                                            ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white"
                                                            : "bg-slate-800/80 text-slate-100"
                                                    } ${turn.speaker === "Customer" ? "ml-auto" : ""}`}
                                                >
                                                    <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{turn.speaker}</div>
                                                    {turn.text}
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>
                                    </div>

                                    {/* Tool-call footer chip */}
                                    <div className="flex flex-wrap items-center gap-2 border-t border-slate-800 px-4 sm:px-5 py-3 text-[11px] text-slate-400">
                                        <span className="rounded-full border border-slate-700 bg-slate-800/60 px-2 py-0.5">tool: shopify.get_order</span>
                                        <span className="rounded-full border border-slate-700 bg-slate-800/60 px-2 py-0.5">tool: shopify.cancel_order</span>
                                        <span className="rounded-full border border-slate-700 bg-slate-800/60 px-2 py-0.5">sms: tracking link</span>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Key Features of Our Shopify AI Voice &amp; Chat Agent</h2>
                        <p className="mt-4 text-slate-300">
                            Built to resolve high-volume Shopify support requests across phone, chat and outbound — quickly and safely.
                        </p>
                    </div>

                    <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {keyFeatures.map((feature, index) => (
                            <article
                                key={feature.title}
                                className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 p-6 shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-1 hover:border-orange-400/40"
                            >
                                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-500 to-pink-500" />
                                <div className="flex items-center justify-between">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/15 border border-orange-400/30">
                                        <svg className="h-5 w-5 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span className="rounded-full border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                </div>

                                <h3 className="mt-5 text-lg font-semibold text-white">{feature.title}</h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">{feature.description}</p>

                                <div className="mt-5 text-xs text-orange-200/90">Shopify support automation</div>
                            </article>
                        ))}
                    </div>
                </section>

                {/* OUTBOUND CALLS POWERED BY SHOPIFY EVENTS */}
                <section id="outbound-triggers" className="relative overflow-hidden border-y border-slate-900 bg-slate-950/60">
                    <div className="absolute inset-0 -z-10 opacity-30">
                        <div className="pointer-events-none absolute -left-32 top-0 h-72 w-72 rounded-full bg-pink-500/20 blur-3xl" />
                        <div className="pointer-events-none absolute -right-32 bottom-0 h-72 w-72 rounded-full bg-amber-500/20 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-20">
                        <div className="text-center max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                Outbound voice triggers
                            </div>
                            <h2 className="mt-5 text-3xl sm:text-4xl font-bold text-white">
                                Outbound Calls Powered by{" "}
                                <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                    Shopify Events
                                </span>
                            </h2>
                            <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                                Connect your Shopify store once. The AI agent dials your customers automatically the moment something happens worth talking about — cart abandoned, high-value order placed, shipment created, refund issued.
                            </p>
                        </div>

                        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                            {outboundTriggers.map((trigger, index) => (
                                <motion.article
                                    key={trigger.title}
                                    initial={{ opacity: 0, y: 14 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.05 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 p-6 shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-1 hover:border-pink-400/40"
                                >
                                    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${trigger.accent}`} />
                                    <div className="flex items-center justify-between">
                                        <div className={`flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-gradient-to-br ${trigger.accent} bg-opacity-20`}>
                                            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h2.28a2 2 0 011.94 1.515l.62 2.48a2 2 0 01-.45 1.95L8.09 10.91a11 11 0 005 5l1.97-1.3a2 2 0 011.95-.45l2.48.62A2 2 0 0121 16.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                            </svg>
                                        </div>
                                        <code className="rounded-md border border-slate-700 bg-slate-950/60 px-2 py-0.5 text-[10px] font-mono text-slate-300">
                                            {trigger.topic}
                                        </code>
                                    </div>
                                    <h3 className="mt-5 text-lg font-semibold text-white">{trigger.title}</h3>
                                    <p className="mt-3 text-slate-300 leading-relaxed">{trigger.description}</p>
                                </motion.article>
                            ))}
                        </div>

                        <div className="mt-10 rounded-2xl border border-slate-800 bg-slate-900/70 p-6 sm:p-7">
                            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-white">Built on Shopify webhooks &amp; the FloGPT Call Pipeline</h3>
                                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                                        Every trigger ships with a Shopify webhook subscription, dedupe key, phone resolver and call script — drop in a pipeline, switch it on, and the agent starts dialing. Setup steps live in the{" "}
                                        <Link to="/docs/shopify" className="font-medium text-orange-300 hover:text-orange-200">
                                            Shopify integration guide
                                        </Link>
                                        .
                                    </p>
                                </div>
                                <Link
                                    to="/ai-voice-agents#outbound-triggers"
                                    className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10 whitespace-nowrap"
                                >
                                    See all trigger sources
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                <section id="book-free-demo" className="mx-auto max-w-6xl px-6 lg:px-8 pb-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white">Get Started with AI Voice &amp; Chat Support for Shopify</h2>
                        <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                            Book a free consultation and see how FloGPT can automate your Shopify support workflow — across phone, chat and outbound triggers.
                        </p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="mt-12 max-w-4xl mx-auto"
                    >
                        <ScheduleBookingForm
                            title="Book a Free Demo"
                            subtitle="Use the same meeting scheduler from our Contact Us page"
                            submitLabel="Book Free Demo"
                        />
                    </motion.div>
                </section>

                <FAQSection
                    id="shopify-support"
                    eyebrow="Shopify · FAQ"
                    heading="Common questions about Shopify AI support"
                    items={SHOPIFY_SUPPORT_FAQS}
                />
            </main>
            <Footer />

            <JsonLd id="service-shopify-support" data={serviceSchema} />
        </div>
    );
}
